import React from 'react';
import { CheckCircle, XCircle, MousePointer, MessageSquare, FileText, Image } from 'lucide-react';

interface FeedbackPin {
  id: string;
  x: number;
  y: number;
  comment: string;
  author?: string;
  timestamp: Date;
}

interface FeedbackPanelProps {
  feedback: string;
  onFeedbackChange: (feedback: string) => void;
  onApprove: () => void;
  onReject: () => void;
  submitting: boolean;
  reviewerType: 'account_manager' | 'client';
  feedbackPins?: FeedbackPin[];
  isPlacingPin?: boolean;
  onTogglePlacePin?: () => void;
  tempPin?: { x: number; y: number } | null;
  pinComment?: string;
  onPinCommentChange?: (comment: string) => void;
  onAddPin?: () => void;
  onCancelPin?: () => void;
  selectedAsset?: any;
  selectedItem?: any;
  assetFeedback?: Record<number, string[]>;
  onAddAssetFeedback?: (assetId: number, feedback: string) => void;
}

function FeedbackPanel({
  feedback,
  onFeedbackChange,
  onApprove,
  onReject,
  submitting,
  reviewerType,
  feedbackPins = [],
  isPlacingPin = false,
  onTogglePlacePin,
  tempPin,
  pinComment = '',
  onPinCommentChange,
  onAddPin,
  onCancelPin,
  selectedAsset,
  selectedItem,
  assetFeedback = {},
  onAddAssetFeedback
}: FeedbackPanelProps) {
  const [assetComment, setAssetComment] = React.useState('');
  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-gray-600" />
            <h3 className="text-sm font-semibold text-gray-700">Feedback</h3>
          </div>
          <button
            onClick={onTogglePlacePin}
            disabled={isPlacingPin}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1 ${
              isPlacingPin
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <MousePointer className="w-3 h-3" />
            Add Pin
          </button>
        </div>
      </div>

      {/* Feedback Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Current Asset/Item Info */}
        {(selectedAsset || selectedItem) && (
          <div className="mb-4 bg-gray-50 rounded-lg p-3">
            <div className="text-xs font-semibold text-gray-600 mb-2">Current Selection</div>
            {selectedItem && (
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-3 h-3 text-gray-500" />
                <span className="text-sm text-gray-700">{selectedItem.title}</span>
              </div>
            )}
            {selectedAsset && (
              <div className="flex items-center gap-2">
                <Image className="w-3 h-3 text-gray-500" />
                <span className="text-sm text-gray-700">{selectedAsset.file_name}</span>
              </div>
            )}
          </div>
        )}

        {/* Asset-Specific Feedback */}
        {selectedAsset && (
          <div className="mb-4">
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Asset Feedback</h4>
            <div className="space-y-2">
              {assetFeedback[selectedAsset.id]?.map((comment, index) => (
                <div key={index} className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-gray-700">{comment}</p>
                </div>
              ))}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={assetComment}
                  onChange={(e) => setAssetComment(e.target.value)}
                  placeholder="Add feedback for this asset..."
                  className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && assetComment && onAddAssetFeedback) {
                      onAddAssetFeedback(selectedAsset.id, assetComment);
                      setAssetComment('');
                    }
                  }}
                />
                <button
                  onClick={() => {
                    if (assetComment && onAddAssetFeedback) {
                      onAddAssetFeedback(selectedAsset.id, assetComment);
                      setAssetComment('');
                    }
                  }}
                  disabled={!assetComment}
                  className="px-3 py-1.5 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pin Comments */}
        {feedbackPins.length > 0 && (
          <div className="mb-4">
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Visual Feedback Pins</h4>
            <div className="space-y-2">
              {feedbackPins.map((pin, index) => (
                <div key={pin.id} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-blue-700">Pin {index + 1}</span>
                        <span className="text-xs text-gray-500">{pin.author}</span>
                      </div>
                      <p className="text-sm text-gray-700">{pin.comment}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pin Input */}
        {tempPin && (
          <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <label className="text-xs font-semibold text-gray-700 block mb-2">
              Add comment for pin:
            </label>
            <input
              type="text"
              value={pinComment}
              onChange={(e) => onPinCommentChange?.(e.target.value)}
              placeholder="Enter your feedback..."
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
            <div className="flex gap-2 mt-2">
              <button
                onClick={onAddPin}
                disabled={!pinComment}
                className="px-3 py-1 bg-blue-500 text-white rounded text-xs font-medium hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save Pin
              </button>
              <button
                onClick={onCancelPin}
                className="px-3 py-1 border border-gray-300 rounded text-xs hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

      </div>

      {/* General Feedback - Moved to bottom */}
      <div className="p-4 border-t bg-gray-50">
        <label className="text-xs font-semibold text-gray-500 uppercase block mb-2">
          General Comments
        </label>
        <textarea
          value={feedback}
          onChange={(e) => onFeedbackChange(e.target.value)}
          placeholder="Add overall feedback for this review..."
          className="w-full p-3 bg-white border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows={3}
        />
      </div>

    </div>
  );
}

export default FeedbackPanel;